import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminTopNavbar from './components/AdminTopNavbar';
import { getAdminProfile, updateAdminProfile, updateAdminPassword } from '../api/adminApi';
import '../styles/admin-unified.css';

// Default profile image URL
const DEFAULT_AVATAR = "https://ui-avatars.com/api/?name=Admin&background=ffd866&color=000";

const AdminProfile = () => {
  const [dropdownActive, setDropdownActive] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    role: '',
    phone: '',
    joinDate: '',
    lastLogin: '',
    avatar: DEFAULT_AVATAR,
    bio: '',
    permissions: [],
    activityLog: []
  });

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    bio: '',
    avatar: DEFAULT_AVATAR
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const data = await getAdminProfile();
      // Set avatar to default if not provided
      const avatarUrl = data.avatar || DEFAULT_AVATAR;
      setProfile({
        ...data,
        avatar: avatarUrl,
        permissions: data.permissions || [], // Ensure permissions is always an array
        activityLog: data.activityLog || [] // Ensure activityLog is always an array
      });
      setFormData({
        name: data.name,
        email: data.email,
        phone: data.phone,
        bio: data.bio,
        avatar: avatarUrl
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch profile');
      toast.error(err.message || 'Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const toggleDropdown = () => {
    setDropdownActive(!dropdownActive);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      name: profile.name,
      email: profile.email,
      phone: profile.phone,
      bio: profile.bio,
      avatar: profile.avatar
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({
      ...passwordData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updatedProfile = {
        ...formData,
        // Only send the avatar if it's a data URL (new upload) or a string URL (existing)
        avatar: formData.avatar.startsWith('data:image') ? formData.avatar : formData.avatar
      };
      
      await updateAdminProfile(updatedProfile);
      setProfile(prev => ({
        ...prev,
        ...updatedProfile
      }));
      setIsEditing(false);
      toast.success('Profile updated successfully');
    } catch (err) {
      setError(err.message || 'Failed to update profile');
      toast.error(err.message || 'Failed to update profile');
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    try {
      await updateAdminPassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      toast.success('Password updated successfully');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      const errorMessage = err.message || 'Failed to update password';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const avatarUrl = reader.result;
        // Update both profile and formData
        setProfile(prev => ({
          ...prev,
          avatar: avatarUrl
        }));
        setFormData(prev => ({
          ...prev,
          avatar: avatarUrl
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <AdminTopNavbar />
        <div className="admin-loading">
          <div className="admin-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <AdminTopNavbar />

      <div className="admin-content pt-20">
        <div className="max-w-7xl mx-auto">
          {/* Tabs */}
          <div className="admin-tabs">
            <ul className="flex -mb-px">
              <li className="mr-2">
                <button
                  className={`admin-tab ${activeTab === 'profile' ? 'active' : ''}`}
                  onClick={() => setActiveTab('profile')}
                >
                  Profile Information
                </button>
              </li>
              <li className="mr-2">
                <button
                  className={`admin-tab ${activeTab === 'security' ? 'active' : ''}`}
                  onClick={() => setActiveTab('security')}
                >
                  Security
                </button>
              </li>
              <li className="mr-2">
                <button
                  className={`admin-tab ${activeTab === 'activity' ? 'active' : ''}`}
                  onClick={() => setActiveTab('activity')}
                >
                  Activity Log
                </button>
              </li>
            </ul>
          </div>

          {activeTab === 'profile' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Profile Info */}
              <div className="md:col-span-1">
                <div className="admin-card">
                  <div className="text-center mb-6">
                    <div className="relative inline-block">
                      <img 
                        src={isEditing ? formData.avatar : profile.avatar} 
                        alt={profile.name} 
                        className="w-32 h-32 rounded-full mx-auto mb-4 object-cover border-4 border-yellow-500/30"
                      />
                      <label 
                        htmlFor="avatar-upload"
                        className="absolute bottom-0 right-0 admin-btn admin-btn-primary p-2 rounded-full cursor-pointer"
                      >
                        📷
                      </label>
                      <input
                        type="file"
                        id="avatar-upload"
                        className="hidden"
                        accept="image/*"
                        onChange={handleAvatarChange}
                      />
                    </div>
                    <h2 className="text-xl font-semibold text-accent">{profile.name}</h2>
                    <p className="text-secondary">{profile.role}</p>
                  </div>

                  {isEditing ? (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="admin-label">Name</label>
                        <input 
                          type="text" 
                          name="name" 
                          value={formData.name} 
                          onChange={handleChange}
                          className="admin-input"
                          required
                        />
                      </div>
                      <div>
                        <label className="admin-label">Email</label>
                        <input 
                          type="email" 
                          name="email" 
                          value={formData.email} 
                          onChange={handleChange}
                          className="admin-input"
                          required
                        />
                      </div>
                      <div>
                        <label className="admin-label">Phone</label>
                        <input 
                          type="text" 
                          name="phone" 
                          value={formData.phone} 
                          onChange={handleChange}
                          className="admin-input"
                        />
                      </div>
                      <div>
                        <label className="admin-label">Bio</label>
                        <textarea 
                          name="bio" 
                          value={formData.bio} 
                          onChange={handleChange}
                          rows="3"
                          className="admin-textarea"
                        ></textarea>
                      </div>
                      <div className="flex justify-end space-x-3">
                        <button 
                          type="button" 
                          onClick={handleCancel}
                          className="admin-btn admin-btn-secondary"
                        >
                          Cancel
                        </button>
                        <button 
                          type="submit"
                          className="admin-btn admin-btn-primary"
                        >
                          Save Changes
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="admin-label">Email</label>
                        <p className="text-secondary">{profile.email}</p>
                      </div>
                      <div>
                        <label className="admin-label">Phone</label>
                        <p className="text-secondary">{profile.phone}</p>
                      </div>
                      <div>
                        <label className="admin-label">Join Date</label>
                        <p className="text-secondary">{profile.joinDate}</p>
                      </div>
                      <div>
                        <label className="admin-label">Last Login</label>
                        <p className="text-secondary">{profile.lastLogin}</p>
                      </div>
                      <div>
                        <label className="admin-label">Bio</label>
                        <p className="text-secondary">{profile.bio}</p>
                      </div>
                      <button 
                        onClick={handleEdit}
                        className="admin-btn admin-btn-primary w-full"
                      >
                        Edit Profile
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Permissions */}
              <div className="md:col-span-2">
                <div className="admin-card">
                  <div className="border-b border-primary pb-4 mb-6">
                    <h3 className="text-lg font-semibold text-accent">Permissions</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {profile.permissions.map((permission, index) => (
                      <div key={index} className="flex items-center space-x-3 p-3 bg-card rounded-lg border border-primary">
                        <span className="text-green-400">✓</span>
                        <span className="text-secondary">{permission}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="max-w-2xl mx-auto">
              <div className="admin-card">
                <div className="border-b border-primary pb-4 mb-6">
                  <h3 className="text-lg font-semibold text-accent">Change Password</h3>
                </div>
                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  <div>
                    <label className="admin-label">Current Password</label>
                    <input 
                      type="password" 
                      name="currentPassword" 
                      value={passwordData.currentPassword} 
                      onChange={handlePasswordChange}
                      className="admin-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="admin-label">New Password</label>
                    <input 
                      type="password" 
                      name="newPassword" 
                      value={passwordData.newPassword} 
                      onChange={handlePasswordChange}
                      className="admin-input"
                      required
                    />
                  </div>
                  <div>
                    <label className="admin-label">Confirm New Password</label>
                    <input 
                      type="password" 
                      name="confirmPassword" 
                      value={passwordData.confirmPassword} 
                      onChange={handlePasswordChange}
                      className="admin-input"
                      required
                    />
                  </div>
                  <div className="flex justify-end">
                    <button 
                      type="submit"
                      className="admin-btn admin-btn-primary"
                    >
                      Update Password
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {activeTab === 'activity' && (
            <div className="admin-card">
              <div className="border-b border-primary pb-4 mb-6">
                <h3 className="text-lg font-semibold text-accent">Recent Activity</h3>
              </div>
              <div className="admin-table">
                <table className="w-full">
                  <thead className="admin-table-header">
                    <tr>
                      <th className="text-left">Action</th>
                      <th className="text-left">Date</th>
                      <th className="text-left">IP Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profile.activityLog.map(activity => (
                      <tr key={activity.id} className="hover:bg-yellow-500/5 transition-colors">
                        <td className="text-secondary">{activity.action}</td>
                        <td className="text-muted">{activity.date}</td>
                        <td className="text-muted">{activity.ip}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;